import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui';
import type { ChatImage } from '@/components/chat';
import {
  emptyAskStreamAccum,
  queryKeys,
  runAskAsync,
  useApi,
  useConversation,
  useMe,
  type ConversationMessage,
} from '@/lib/api';
import { cn, usePrefersReducedMotion } from '@/lib/utils';
import { notifyResponseCompleted } from '@/lib/notifications';
import { ShibaLauncher, type ShibaUnread } from './ShibaLauncher';
import type { ShibaMessage } from './ShibaMessage';
import { ShibaPanel } from './ShibaPanel';
import { useAutoMinimize } from './useAutoMinimize';
import { useShibaFrame } from './useShibaFrame';
import './Shiba.css';

/**
 * A boas-vindas da Shiba. É o único texto de interface que escreve o nome da
 * plataforma por extenso fora de `lib/seo` — por isso é exportado: testes e
 * specs E2E importam esta constante em vez de repetir a frase.
 */
export const SHIBA_GREETING =
  'Sou a Shiba, sua assistente na Shiba IA. Pergunte sobre a sua base de conhecimento ou a plataforma.';

/** localStorage key for the open/collapsed preference. */
const OPEN_KEY = 'habilita.shiba-open';

/**
 * sessionStorage key for the thread the widget is on — ONLY its id: the turns
 * come back from the history (`/v1/conversations/{id}`), so no question text
 * sits in the browser. Session scope keeps the thread across a reload (F5) and
 * SPA navigation, while a new tab starts a new one.
 */
const CONVERSATION_KEY = 'habilita.shiba-conversation';

/**
 * sessionStorage key do "Ocultar". Sessão, nunca localStorage: ocultar é tirar a
 * pílula do caminho AGORA (a navegação e o F5 da aba respeitam), não desligar o
 * assistente para sempre — numa aba ou sessão nova ela volta, e no meio do
 * caminho o menu da conta ("Perguntar à Shiba") a chama de volta.
 */
const HIDDEN_KEY = 'habilita.shiba-hidden';

/** O evento pelo qual {@link summonShiba} chega ao widget montado. */
const SUMMON_EVENT = 'habilita:shiba-summon';

/** Quanto tempo o toast de "Shiba oculta" (com o Desfazer) fica na tela. */
const HIDE_TOAST_MS = 8000;

/**
 * Chama a Shiba: desfaz o "Ocultar" e abre o painel com o cursor no compositor.
 * É o item "Perguntar à Shiba" do menu da conta — o caminho de volta de quem
 * ocultou a pílula. Um evento, e não estado compartilhado, porque o widget é
 * dono da própria visibilidade e o menu só precisa pedir.
 */
export function summonShiba(): void {
  window.dispatchEvent(new Event(SUMMON_EVENT));
}

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function readHidden(): boolean {
  try {
    return sessionStorage.getItem(HIDDEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function readConversationId(): string | null {
  try {
    return sessionStorage.getItem(CONVERSATION_KEY);
  } catch {
    return null;
  }
}

/** A recorded turn as the panel renders it: the question bubble, then the answer. */
function messageFromHistory(message: ConversationMessage): ShibaMessage {
  const base = { ...emptyAskStreamAccum(), id: message.id, status: 'done' as const };
  return message.role === 'user'
    ? { ...base, role: 'user', question: message.text }
    : {
        ...base,
        role: 'assistant',
        question: '',
        answer: message.text,
        sources: message.sources ?? [],
        confidence: null,
        interactionId: message.interactionId,
      };
}

const WELCOME: ShibaMessage = {
  id: 'shiba-welcome',
  role: 'assistant',
  question: '',
  ...emptyAskStreamAccum(),
  status: 'done',
  answer: SHIBA_GREETING,
};

/** Announced politely when something lands with the panel collapsed. */
const ANNOUNCE = {
  answer: 'Nova resposta da Shiba',
  failure: 'A Shiba não conseguiu responder. Abra o painel para tentar de novo.',
} as const;

/**
 * The floating "Shiba" assistant, available on every authenticated screen
 * (mounted by the AppShell). It is one draggable surface that switches between a
 * pill and a conversation panel answered by the same background RAG flow as the
 * Knowledge chat (202 + polling, level `completa`, channel `web`), recorded in a
 * `chatbot` thread of the history. Answers — and failures — that land while it is
 * collapsed raise the unread dot, so a poll in flight keeps running across a
 * collapse and is only cancelled on unmount. The open/collapsed preference
 * persists in localStorage; only the thread's ID persists in SESSION storage, so
 * a reload (F5) reads the turns back from the history — a turn still cooking at
 * the reload shows up there once it lands.
 *
 * FOCO: o foco só se move quando o USUÁRIO move o estado. Abrir pela ação entrega
 * o cursor ao compositor; recolher por Escape ou pelo controle devolve o foco à
 * pílula. As duas transições que o usuário não pediu — a hidratação da preferência
 * salva e o auto-minimizar, cuja pré-condição é justamente o foco estar em OUTRO
 * lugar — nunca tocam no foco.
 *
 * "Nova conversa" (ver {@link newConversation}) troca de conversa sem mexer no
 * quadro: posição, tamanho e a preferência aberto/recolhido continuam.
 *
 * "Ocultar" (na pílula) tira o widget da tela pelo resto da sessão da aba; os
 * turnos em voo continuam e as novidades seguem anunciadas. Oculta implica
 * recolhida: a volta, por {@link summonShiba}, já abre o painel.
 */
export function ShibaWidget(): JSX.Element {
  const [hidden, setHidden] = useState(readHidden);
  const [open, setOpen] = useState(() => !readHidden() && readOpen());
  /** Open *because the user opened it* — false while restored from the preference. */
  const [openedByUser, setOpenedByUser] = useState(false);
  /**
   * A troca pílula ⇄ painel tem um crossfade, mas só depois que alguém troca de
   * estado: a pílula (ou o painel restaurado) que aparece com a página não
   * entra animada — sem coreografia no carregamento (DESIGN.md, Motion).
   */
  const [animate, setAnimate] = useState(false);
  /** The thread the turns land in; null until the first question opens one. */
  const [conversationId, setConversationId] = useState(readConversationId);
  /** The turns asked in this mount; the recorded ones come from the history. */
  const [live, setLive] = useState<ShibaMessage[]>([]);
  const [unread, setUnread] = useState<ShibaUnread>(null);
  const [announcement, setAnnouncement] = useState('');
  const idRef = useRef(0);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const wasOpenRef = useRef(open);
  /** Only a collapse the user asked for hands the focus back to the pill. */
  const restoreFocusRef = useRef(false);
  // The poll loop outlives the render that started it; it needs the live state.
  const openRef = useRef(open);
  openRef.current = open;
  // Each turn's cancel handle: unmounting stops every poll in flight, and a
  // retry stops the one it replaces. A collapse is NOT a cancel — the unread
  // dot exists precisely for answers that land out of sight.
  const cancelsRef = useRef(new Map<string, () => void>());
  useEffect(() => {
    const cancels = cancelsRef.current;
    return () => cancels.forEach((cancel) => cancel());
  }, []);

  const frame = useShibaFrame(open, usePrefersReducedMotion());
  const api = useApi();
  const me = useMe();
  const qc = useQueryClient();
  const { toast } = useToast();
  // The ask reads the thread when it is SENT, which may be a render or two after
  // the 202 of the previous turn named it.
  const conversationRef = useRef(conversationId);
  conversationRef.current = conversationId;

  const thread = useConversation(conversationId);
  // A turn just answered comes back with the thread on the next refetch; the
  // live copy wins because it holds what the history drops (the score).
  const answeredHere = new Set(live.flatMap((m) => (m.interactionId ? [m.interactionId] : [])));
  const history = (thread.data?.messages ?? [])
    .filter((m) => !answeredHere.has(m.interactionId))
    .map(messageFromHistory);
  const messages = [WELCOME, ...history, ...live];

  // An archived (or otherwise gone) thread is a 404: start over instead of
  // pinning the widget to an error.
  useEffect(() => {
    if (thread.isError) setConversationId(null);
  }, [thread.isError]);

  useEffect(() => {
    try {
      if (conversationId) sessionStorage.setItem(CONVERSATION_KEY, conversationId);
      else sessionStorage.removeItem(CONVERSATION_KEY);
    } catch {
      // Quota or privacy mode: the thread keeps working, it just won't survive F5.
    }
  }, [conversationId]);

  const expand = (): void => {
    setOpen(true);
    setOpenedByUser(true);
    setAnimate(true);
    setUnread(null);
    setAnnouncement('Painel da Shiba aberto');
  };

  /**
   * `byUser` decides the focus: Escape and the collapse control return it to the
   * pill action, while the auto-minimize timer must never touch it — its own
   * precondition is that the user is working somewhere else, and stealing the
   * focus there eats keystrokes (and turns the next Space into a re-open).
   */
  const collapse = (byUser: boolean): void => {
    restoreFocusRef.current = byUser;
    setOpen(false);
    setOpenedByUser(false);
    setAnimate(true);
    setAnnouncement('Painel da Shiba recolhido');
  };

  /**
   * Some com a pílula. O toast diz o caminho de volta — sem ele, quem ocultou não
   * teria pista de onde a Shiba foi parar — e oferece o Desfazer para o clique
   * acidental. O toast é a região viva aqui (fora do app, segue anunciado).
   */
  const hide = (): void => {
    setHidden(true);
    toast({
      tone: 'info',
      title: 'Shiba oculta',
      description: 'Para chamá-la de volta, abra o menu da sua conta, no pé da barra lateral, e escolha "Perguntar à Shiba".',
      action: { label: 'Desfazer', onClick: () => setHidden(false) },
      durationMs: HIDE_TOAST_MS,
    });
  };

  useAutoMinimize(frame.rootRef, open, () => collapse(false));

  useEffect(() => {
    try {
      if (hidden) sessionStorage.setItem(HIDDEN_KEY, 'true');
      else sessionStorage.removeItem(HIDDEN_KEY);
    } catch {
      // Modo privado ou cota: ocultar continua valendo até o próximo carregamento.
    }
  }, [hidden]);

  // O menu da conta chama a Shiba de volta (e a abre) por um evento: ver summonShiba.
  useEffect(() => {
    const onSummon = (): void => {
      setHidden(false);
      expand();
    };
    window.addEventListener(SUMMON_EVENT, onSummon);
    return () => window.removeEventListener(SUMMON_EVENT, onSummon);
    // `expand` só chama setters, que são estáveis: o ouvinte do primeiro render serve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, String(open));
    } catch {
      // Non-fatal; preference simply won't survive reloads.
    }
  }, [open]);

  // Esc recoils the panel — but only when the widget owns the focus. Whoever holds
  // it owns the Escape: the conversation search or a modal on top of the page closes
  // itself, and one keypress must never dismiss two surfaces.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (!frame.rootRef.current?.contains(document.activeElement)) return;
      collapse(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Hand focus back to the pill action — only after a collapse the user asked for.
  useEffect(() => {
    if (wasOpenRef.current && !open && restoreFocusRef.current) ctaRef.current?.focus();
    restoreFocusRef.current = false;
    wasOpenRef.current = open;
  }, [open]);

  const update = (id: string, patch: Partial<ShibaMessage>): void => {
    setLive((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  /** Raise the pill's dot for a turn that finished out of sight. */
  const markUnread = (kind: Exclude<ShibaUnread, null>): void => {
    if (openRef.current) return;
    setUnread(kind);
    setAnnouncement(ANNOUNCE[kind]);
  };

  const runAsk = (id: string, question: string, image: ChatImage | undefined): void => {
    cancelsRef.current.get(id)?.();
    const cancel = runAskAsync(
      api,
      {
        question,
        level: 'completa',
        channel: 'web',
        // Só o que o servidor precisa: o nome do arquivo é enfeite da conversa.
        image: image ? { dataB64: image.dataB64, mimetype: image.mimetype } : undefined,
        surface: 'chatbot',
        conversationId: conversationRef.current ?? undefined,
      },
      {
        // The 202 names the thread — the new one, on the first question.
        onAccepted: (accepted) => setConversationId(accepted.conversationId),
        onProgress: (message) => update(id, { progress: message }),
        onDone: (result) => {
          cancelsRef.current.delete(id);
          update(id, { ...result, status: 'done', progress: undefined });
          notifyResponseCompleted(me.data, result.answer);
          markUnread('answer');
          void qc.invalidateQueries({ queryKey: queryKeys.interactions.all });
          void qc.invalidateQueries({ queryKey: queryKeys.conversations.all });
          // A resposta gasta a cota do mês: o /me relê o consumo junto.
          void qc.invalidateQueries({ queryKey: queryKeys.me });
        },
        onFailed: (error) => {
          cancelsRef.current.delete(id);
          // A failure out of sight is still news: silently leaving the pill at rest
          // strands a retry nobody knows about.
          update(id, { status: 'error', error, progress: undefined });
          markUnread('failure');
        },
      },
    );
    cancelsRef.current.set(id, cancel);
  };

  const send = (question: string, image?: ChatImage): void => {
    const trimmed = question.trim();
    if (!trimmed) return;
    const n = (idRef.current += 1);
    const answerId = `shiba-${n}-a`;
    setLive((prev) => [
      ...prev,
      {
        id: `shiba-${n}`,
        role: 'user',
        question: trimmed,
        image,
        ...emptyAskStreamAccum(),
        status: 'done',
      },
      // The answer turn keeps the image too: a retry resends the WHOLE question.
      { id: answerId, role: 'assistant', question: trimmed, image, ...emptyAskStreamAccum() },
    ]);
    void runAsk(answerId, trimmed, image);
  };

  /**
   * Recomeçar do zero: a próxima pergunta abre outra conversa no histórico (a
   * atual continua lá). Sem confirmação: nada se perde. O turno em voo é
   * cancelado antes de a lista ser trocada, senão o poll dele aterrissaria numa
   * conversa que não está mais na tela. O contador de ids NÃO volta ao zero de
   * propósito: um callback atrasado de turno cancelado nunca pode casar com uma
   * mensagem nova. Posição, tamanho e a preferência aberto/recolhido ficam onde
   * estavam — só o conteúdo recomeça.
   */
  const newConversation = (): void => {
    cancelsRef.current.forEach((cancel) => cancel());
    cancelsRef.current.clear();
    setLive([]);
    setConversationId(null);
    setAnnouncement('Nova conversa iniciada.');
    // Um chat vazio existe para ser escrito: o cursor vai para o compositor, que
    // continua montado (o painel não fecha).
    composerRef.current?.focus();
  };

  const retry = (message: ShibaMessage): void => {
    update(message.id, { ...emptyAskStreamAccum(), progress: undefined, error: undefined });
    void runAsk(message.id, message.question, message.image);
  };

  const sending = messages.some(
    (m) => m.role === 'assistant' && (m.status === 'pending' || m.status === 'streaming'),
  );

  // Oculta, o quadro continua montado (vazio, transparente e sem ponteiro, como
  // na pílula): a região `status` não pode sair do DOM, senão a novidade que
  // chegar nesse meio-tempo não é anunciada.
  return (
    <div
      ref={frame.rootRef}
      className={cn(
        'shiba',
        open ? 'shiba--panel' : 'shiba--pill',
        frame.dragging && 'shiba--dragging',
        animate && 'shiba--animate',
      )}
      style={frame.style}
    >
      {open ? (
        <ShibaPanel
          messages={messages}
          sending={sending}
          focusOnMount={openedByUser}
          composerRef={composerRef}
          onSend={send}
          onNewConversation={newConversation}
          onRetry={retry}
          onClose={() => collapse(true)}
          onGrab={frame.gripProps.onPointerDown}
        />
      ) : (
        !hidden && (
          <ShibaLauncher
            onOpen={expand}
            unread={unread}
            ctaRef={ctaRef}
            onGrab={frame.gripProps.onPointerDown}
            onHide={hide}
          />
        )
      )}

      <p className="sr-only" role="status">
        {announcement}
      </p>
    </div>
  );
}
